// src/pages/ccp/PrepCcpConfigPage.js
// Per-intermediate-product cook+cool critical limits — see docs/PREP_CCP_MODULE.md. Unlike Baking
// CCP's single company-wide config, each intermediate product gets its own limits since different
// fillings/sauces need different safe cook temps and cooling windows.
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import prepCcpService from '../../services/prepCcpService';
import productService from '../../services/productService';
import SearchableSelect from '../../components/SearchableSelect';
import { useLanguage, t } from '../../context/LanguageContext';

const BLANK_FORM = {
  cookMinTempF: 165,
  cookHoldMinutes: 0,
  coolStage1MaxMinutes: 360,
  coolStage1TargetTempF: 70,
  coolStage2MaxMinutes: 240,
  coolStage2TargetTempF: 41,
};

export default function PrepCcpConfigPage() {
  const { lang } = useLanguage();
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [form, setForm] = useState(BLANK_FORM);

  useEffect(() => {
    productService.getProducts({ includeInactive: true }).then(data => {
      const list = (Array.isArray(data) ? data : data?.products || []).filter(p => p.isIntermediate);
      setProducts(list);
    }).catch(() => setProducts([]));
  }, []);

  const loadConfig = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    setError('');
    setSaveStatus('');
    try {
      const cfg = await prepCcpService.getConfig(pid);
      setForm({
        cookMinTempF: cfg.cookMinTempF ?? BLANK_FORM.cookMinTempF,
        cookHoldMinutes: cfg.cookHoldMinutes ?? BLANK_FORM.cookHoldMinutes,
        coolStage1MaxMinutes: cfg.coolStage1MaxMinutes ?? BLANK_FORM.coolStage1MaxMinutes,
        coolStage1TargetTempF: cfg.coolStage1TargetTempF ?? BLANK_FORM.coolStage1TargetTempF,
        coolStage2MaxMinutes: cfg.coolStage2MaxMinutes ?? BLANK_FORM.coolStage2MaxMinutes,
        coolStage2TargetTempF: cfg.coolStage2TargetTempF ?? BLANK_FORM.coolStage2TargetTempF,
      });
    } catch {
      setError(t('Failed to load configuration.', 'Error al cargar la configuración.', lang));
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => { if (productId) loadConfig(productId); }, [productId, loadConfig]);

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('');
    try {
      await prepCcpService.saveConfig(productId, form);
      setSaveStatus('success');
    } catch {
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-4" style={{ maxWidth: 680 }}>
      <div className="d-flex align-items-center gap-2 mb-4">
        <Link to="/ccp/prep/start" className="text-muted text-decoration-none" style={{ fontSize: 13 }}>
          ← {t('Prep CCP', 'Prep CCP', lang)}
        </Link>
      </div>
      <h4 className="fw-bold mb-1">{t('Prep CCP — Critical Limits', 'Prep CCP — Límites Críticos', lang)}</h4>
      <p className="text-muted small mb-4">
        {t('Each intermediate product (sauce/filling) has its own cook and two-stage cooling limits, since different products have different safe temperatures.',
           'Cada producto intermedio (salsa/relleno) tiene sus propios límites de cocción y enfriamiento en dos etapas, ya que diferentes productos tienen diferentes temperaturas seguras.', lang)}
      </p>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <label className="form-label fw-semibold">{t('Intermediate Product', 'Producto Intermedio', lang)}</label>
          <SearchableSelect
            options={products.map(p => ({ value: String(p.id), label: p.name }))}
            value={productId}
            onChange={setProductId}
            placeholder={t('Select an intermediate product…', 'Selecciona un producto intermedio…', lang)}
          />
          {products.length === 0 && (
            <div className="form-text">
              {t('No intermediate products yet — mark one in Products (Edit Product → "Mark as intermediate").',
                 'Aún no hay productos intermedios — marca uno en Productos (Editar Producto → "Marcar como intermedio").', lang)}
            </div>
          )}
        </div>
      </div>

      {productId && (
        <div className="card shadow-sm">
          <div className="card-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            {loading ? (
              <div className="text-muted">{t('Loading…', 'Cargando…', lang)}</div>
            ) : (
              <>
                <h6 className="fw-bold mb-3">🔥 {t('Cook Step', 'Paso de Cocción', lang)}</h6>
                <div className="row g-3 mb-4">
                  <div className="col-sm-6">
                    <label className="form-label small">{t('Minimum internal temp (°F)', 'Temp. interna mínima (°F)', lang)}</label>
                    <input type="number" className="form-control" value={form.cookMinTempF}
                      onChange={e => setField('cookMinTempF', e.target.value)} />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label small">{t('Hold time at that temp (minutes)', 'Tiempo de retención a esa temp. (minutos)', lang)}</label>
                    <input type="number" className="form-control" value={form.cookHoldMinutes}
                      onChange={e => setField('cookHoldMinutes', e.target.value)} />
                  </div>
                </div>

                <h6 className="fw-bold mb-3">❄️ {t('Cooling — Stage 1', 'Enfriamiento — Etapa 1', lang)}</h6>
                <p className="text-muted small">
                  {t('e.g. USDA: 130°F → 70°F within 6 hours.', 'ej. USDA: 130°F → 70°F en 6 horas.', lang)}
                </p>
                <div className="row g-3 mb-4">
                  <div className="col-sm-6">
                    <label className="form-label small">{t('Target temp (°F)', 'Temp. objetivo (°F)', lang)}</label>
                    <input type="number" className="form-control" value={form.coolStage1TargetTempF}
                      onChange={e => setField('coolStage1TargetTempF', e.target.value)} />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label small">{t('Max time (minutes)', 'Tiempo máx. (minutos)', lang)}</label>
                    <input type="number" className="form-control" value={form.coolStage1MaxMinutes}
                      onChange={e => setField('coolStage1MaxMinutes', e.target.value)} />
                  </div>
                </div>

                <h6 className="fw-bold mb-3">❄️❄️ {t('Cooling — Stage 2', 'Enfriamiento — Etapa 2', lang)}</h6>
                <p className="text-muted small">
                  {t('e.g. USDA: 70°F → 41°F or below within an additional 4 hours.', 'ej. USDA: 70°F → 41°F o menos en 4 horas adicionales.', lang)}
                </p>
                <div className="row g-3 mb-4">
                  <div className="col-sm-6">
                    <label className="form-label small">{t('Target temp (°F)', 'Temp. objetivo (°F)', lang)}</label>
                    <input type="number" className="form-control" value={form.coolStage2TargetTempF}
                      onChange={e => setField('coolStage2TargetTempF', e.target.value)} />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label small">{t('Max time (minutes)', 'Tiempo máx. (minutos)', lang)}</label>
                    <input type="number" className="form-control" value={form.coolStage2MaxMinutes}
                      onChange={e => setField('coolStage2MaxMinutes', e.target.value)} />
                  </div>
                </div>

                {saveStatus === 'success' && <div className="alert alert-success py-2">{t('Saved.', 'Guardado.', lang)}</div>}
                {saveStatus === 'error' && <div className="alert alert-danger py-2">{t('Failed to save.', 'Error al guardar.', lang)}</div>}
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? t('Saving…', 'Guardando…', lang) : t('Save Limits', 'Guardar Límites', lang)}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
